import { FC, useContext, useEffect, useState } from 'react';
import { Avatar, Box, Button, Divider, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { CampaignMode, Media } from '../../interfaces/campaign';
import PlatformCard from './PlatformCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { formatPricePersian } from '../../utils/number';
import usePageCardColors from '../../hooks/usePageCardColors';
import { isCampaignCreateMode } from '../../utils/string';
import { CampaignContext } from '../../contexts/CampaignContext';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';

type ITotalSelectable = {
	onOpenModal?: () => void;
	mode: CampaignMode;
};

const TotalSelectable: FC<ITotalSelectable> = ({ mode, onOpenModal }) => {
	const { t } = useTranslation();
	const { selectedPlatform, fetchedMedias, selectedMediaTypeId, selectedPages, selectedPackages, selectedPlatformId, fetchedCampaign, setViewSelectedItemsMode } = useContext(CampaignContext);
	const [searchParams] = useSearchParams();
	const media_params = searchParams.get('media');

	const [isVisible, setIsVisible] = useState(false);
	const isCreateMode = isCampaignCreateMode(mode);

	const platform = isCreateMode ? selectedPlatform : fetchedCampaign?.media;
	const filterWithType = fetchedMedias?.find(item => item.system_name === media_params);
	const filteredPlatform = fetchedMedias?.find(media => {
		if (selectedPlatformId) {
			return media.id === selectedPlatformId;
		} else {
			return media.id === filterWithType?.id;
		}
	});
	const { bgClass, dividerColor, pageCardBgClass, pageCardBorderColor, viewButtonBorderColor, viewButtonTextColor } = usePageCardColors(platform!);
	const selectedPackagesList = isCreateMode ? selectedPackages : fetchedCampaign?.packages;
	const selectedPagesList = isCreateMode ? selectedPages : fetchedCampaign?.entities;
	const selectedItems = [...(selectedPackagesList as any), ...(selectedPagesList as any)];

	const totalPackagesLength = selectedPackagesList?.length;
	const totalPagesLength = selectedPagesList?.length;
	const totalSelectedLength = totalPackagesLength! + totalPagesLength!;
	const isViewTypeId = selectedMediaTypeId === 'view';
	const isSmsPanel = platform === 'sms' || platform === 'media_growth';

	const handleOpenViewSelectedModal = () => {
		setViewSelectedItemsMode(true);
		onOpenModal?.();
	};

	const debouncedVisibilityToggle = debounce(() => {
		setIsVisible(!!platform);
	}, 300);

	useEffect(() => {
		debouncedVisibilityToggle();
		return () => {
			debouncedVisibilityToggle.cancel();
		};
	}, [platform]);
	const renderSelectedItems = () => (
		<Box className="flex gap-x-4">
			{selectedItems?.slice(0, 3).map(item => (
				<Box key={item?.id} className={`flex min-w-40 items-center justify-between rounded-lg bg-gradient-to-br from-background-paper ${pageCardBorderColor} p-[1px]`}>
					<Box className="h-full w-full rounded-lg bg-background-paper">
						<Box className={`flex h-full w-full flex-col items-center justify-between rounded-lg px-3 py-4 ${pageCardBgClass}`}>
							<Avatar alt={item?.title} src={item?.image} className="mb-3 h-12 w-12" />
							<Typography className="text-base font-medium text-text-primary">{item?.title}</Typography>
							{item?.handle_name && <Typography className="mb-4 mt-2 text-sm text-text-secondary">{item.handle_name}</Typography>}
							<Box className="flex items-center">
								<AccountBalanceWalletIcon className="me-2 text-grey-400" />
								{item && item.price && <Trans i18nKey="tooman_amount_label" values={{ amount: formatPricePersian(item.price) }} components={[<Typography className="text-sm text-text-primary" />]} />}
							</Box>
						</Box>
					</Box>
				</Box>
			))}
		</Box>
	);

	const renderActionButton = () => (
		<>
			<Button onClick={handleOpenViewSelectedModal} variant="outlined" className={`h-14 border ${viewButtonBorderColor} text-sm font-normal ${viewButtonTextColor}`} endIcon={<KeyboardArrowLeftIcon className="text-xl" />}>
				<Trans i18nKey="view_selected_items" count={totalSelectedLength} />
			</Button>
			{isCreateMode && (
				<Button variant="contained" className={`h-14 text-xl font-normal ${bgClass}`} onClick={onOpenModal}>
					{t('select')}
				</Button>
			)}
		</>
	);

	const renderEmptyState = () => {
		const types = fetchedMedias?.filter(media => media.system_name === platform)[0]?.types;
		const messages = types?.filter(type => type.id === selectedMediaTypeId)[0]?.messages;

		return (
			<Box className={`flex flex-col items-start ${isViewTypeId ? 'justify-center' : ''}`}>
				<span className="mb-4 text-xl text-text-primary">{messages?.title}</span>
				<span className="text-base text-text-secondary">{messages?.sub_title}</span>
				{isCreateMode && !isViewTypeId && !isSmsPanel && (
					<Button sx={{ boxShadow: 3 }} className={`${bgClass} mt-9 h-14 w-44 text-xl font-normal`} onClick={onOpenModal}>
						{t('select')}
					</Button>
				)}
			</Box>
		);
	};
	return (
		<>
			{filteredPlatform && (
				<Box className={`mt-4 flex flex-col gap-x-4 rounded-lg bg-background-paper p-6`}>
					<Typography className="mb-6 text-2xl text-text-primary">{t('total_selectable_title')}</Typography>

					{platform && (
						<Box className={`transform transition-all duration-1000 ease-in-out ${isVisible ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'} flex h-full overflow-hidden`}>
							<PlatformCard platform={platform!} />
							<Divider orientation="vertical" className={`mx-12 h-[180px] rounded-lg border-2 ${dividerColor}`} />
							{totalSelectedLength ? (
								<Box className="ms-4 flex w-full justify-between">
									{renderSelectedItems()}
									<Box className="ms-4 flex flex-col justify-end gap-y-4">{renderActionButton()}</Box>
								</Box>
							) : (
								renderEmptyState()
							)}
						</Box>
					)}

					{isCreateMode && !selectedPlatform && <Box className="flex flex-wrap items-center justify-center gap-4">{fetchedMedias?.map(item => <PlatformCard platform={item?.system_name as Media} />)}</Box>}
				</Box>
			)}
		</>
	);
};

export default TotalSelectable;
